import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Printer, IdCard, Receipt, Trophy } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { printAttestation, printMemberCard, printPlayerCard, printFinanceReport, printPayslip } from "../lib/documents";

const DOC_TYPES = [
  { key: "attestation", label: "Attestation de travail", icon: FileText, source: "employees", color: "#1976D2" },
  { key: "payslip", label: "Bulletin de paie", icon: Receipt, source: "payslips", color: "#2E7D32" },
  { key: "membercard", label: "Carte de membre", icon: IdCard, source: "members", color: "#C62828" },
  { key: "playercard", label: "Fiche joueur", icon: Trophy, source: "players", color: "#FBC02D" },
  { key: "financereport", label: "Rapport financier", icon: FileText, source: "report", color: "#7B1FA2" },
];

export default function DocumentCenter() {
  const { user } = useAuth();
  const [docType, setDocType] = useState("attestation");
  const [items, setItems] = useState({ employees: [], payslips: [], members: [], players: [] });

  useEffect(() => {
    Promise.all([
      api.get("/employees").then(r => r.data).catch(() => []),
      api.get("/payslips").then(r => r.data).catch(() => []),
      api.get("/members").then(r => r.data).catch(() => []),
      api.get("/players").then(r => r.data).catch(() => []),
    ]).then(([employees, payslips, members, players]) => {
      setItems({ employees, payslips, members, players });
    });
  }, []);

  const handlePrint = async (key, item) => {
    if (key === "attestation") printAttestation(user.club, item);
    else if (key === "membercard") printMemberCard(user.club, item);
    else if (key === "payslip") {
      const emp = items.employees.find(e => e.id === item.employee_id);
      if (emp) printPayslip(user.club, emp, item);
    } else if (key === "playercard") {
      const { data: stats } = await api.get(`/performances/aggregate/${item.id}`).catch(() => ({ data: null }));
      printPlayerCard(user.club, item, stats);
    } else if (key === "financereport") {
      const [txs, summary] = await Promise.all([
        api.get("/finance/transactions").then(r => r.data),
        api.get("/finance/summary").then(r => r.data),
      ]);
      printFinanceReport(user.club, summary, txs, "Toutes périodes");
    }
  };

  const currentType = DOC_TYPES.find(d => d.key === docType);
  const source = docType === "financereport" ? [{ id: "global", label: "Rapport global" }] :
                 docType === "attestation" ? items.employees.map(e => ({ ...e, label: `${e.first_name} ${e.last_name} — ${e.position}` })) :
                 docType === "payslip" ? items.payslips.map(p => ({ ...p, label: `${p.employee_name} — ${p.month}/${p.year}` })) :
                 docType === "membercard" ? items.members.map(m => ({ ...m, label: `${m.first_name} ${m.last_name} — ${m.member_number}` })) :
                 items.players.map(p => ({ ...p, label: `${p.first_name} ${p.last_name}` }));

  return (
    <div>
      <PageHeader
        overline="Bibliothèque"
        title="Centre de documents"
        description="Tous les documents officiels du club, prêts à imprimer en PDF."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {DOC_TYPES.map(t => {
          const Icon = t.icon;
          const active = docType === t.key;
          return (
            <button key={t.key} onClick={() => setDocType(t.key)}
              data-testid={`doc-type-${t.key}`}
              className={`text-left p-4 rounded-md border transition ${active ? "border-[#C62828] bg-[#C62828]/5" : "border-[#E0E0E0] bg-white hover:border-[#C62828]/40"}`}>
              <div className="w-9 h-9 rounded-md flex items-center justify-center mb-2" style={{ backgroundColor: `${t.color}15`, color: t.color }}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="font-semibold text-sm">{t.label}</p>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
        <h3 className="font-display text-lg font-bold mb-4">{currentType.label}</h3>
        {source.length === 0 ? (
          <p className="text-sm text-[#616161]">Aucun élément disponible. Créez d'abord des entrées dans le module correspondant.</p>
        ) : (
          <div className="space-y-2" data-testid="document-items">
            {source.map((item) => (
              <div key={item.id} className="flex items-center justify-between border border-[#E0E0E0] rounded-md px-4 py-3">
                <p className="text-sm font-medium">{item.label}</p>
                <Button size="sm" onClick={() => handlePrint(docType, item)}
                  className="btn-club" data-testid={`print-doc-${item.id}`}>
                  <Printer className="w-3.5 h-3.5 mr-2" /> Générer PDF
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
